import React, { Fragment } from 'react';

import type { ExtensionHandler } from '@atlaskit/editor-common/extensions/extension-handler';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import type { AnnotationProviders } from '@atlaskit/editor-common/types/annotation';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { ClassicEditorContextProviderLazyLoader } from '../../components/ClassicEditorContextProvider/ClassicEditorContextProviderLazyLoader.ts';
import { ClassicEditorContextProviderLoader } from '../../components/ClassicEditorContextProvider/index.js';
import EditorLoaderLoader from '../../components/EditorLoader/EditorLoaderLoader.js';
import type { DataSourceProvider as ReferentialityDataSourceProvider } from '@atlassian/editor-referentiality/data-source-provider';
import type { ExtensionHandlerWithReferenceFn } from '@atlassian/editor-referentiality/types';
import { RedactedPlaceholderExtensionHandler } from '@confluence/external-share';
import { SessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import {
	getMacroAttributesFromADFNode,
	RENDERER,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import { UserProfileMacroHandler } from '@confluence/profile-macros/entry-points/UserProfileMacroRenderer.ts';
import { ProfilePictureMacroHandler } from '@confluence/profile-macros/entry-points/ProfilePictureMacroRenderer.ts';
import { RecentlyUpdatedMacroHandler } from '@confluence/recently-updated-macro/entry-points/RecentlyUpdatedMacroRenderer.ts';
import { TasksReportMacroHandler } from '@confluence/tasks-report-macro/entry-points/TasksReportMacroRenderer.ts';
import { GlobalQuestionsListMacroHandler } from '@confluence/questions/entry-points/GlobalQuestionsListMacroHandler.ts';
import { ContributorsMacroHandler } from '@confluence/contributors-macro/entry-points/ContributorsMacroRenderer.ts';
import { PopularLabelsMacroHandler } from '@confluence/popular-labels-macro/entry-points/PopularLabelsMacroRenderer.ts';
import { ViewPdfMacroHandler } from '@confluence/viewpdf-macro/entry-points/ViewPdfMacroRenderer.ts';
import { WidgetMacroHandler } from '@confluence/widget-macro/entry-points/WidgetMacroRenderer.ts';
import { IFrameMacroHandler } from '@confluence/iframe-macro/entry-points/IFrameMacroRenderer.ts';
import { ContentByLabelExtensionHandler } from '@confluence/fabric-extension-handlers/entry-points/ContentByLabel.ts';
import { BlogPostsExtensionHandler } from '@confluence/fabric-extension-handlers/entry-points/BlogPosts.ts';
import { PageIncludeExtensionHandler } from '@confluence/fabric-extension-handlers/entry-points/PageInclude.ts';
import { ContentReportTableMacroHandler } from '@confluence/content-report-table-macro/entry-points/ContentReportTableMacroRenderer.ts';
import { AttachmentsMacroHandler } from '@confluence/attachments-macro/entry-points/AttachmentsMacroRenderer.ts';
import { ChangeHistoryMacroHandler } from '@confluence/change-history-macro/entry-points/ChangeHistoryMacroRenderer.ts';
import { PageTreeExtensionHandler } from '@confluence/fabric-extension-handlers/entry-points/PageTree.ts';
import { PageTreeSearchMacroHandler } from '@confluence/pagetree-search-macro/entry-points/PageTreeSearchMacroRenderer.ts';
import {
	DebouncedLegacyMacroRenderer,
	LegacyMacroRenderer,
} from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererQuery.ts';
import { ExcerptIncludeExtensionHandler } from '@confluence/fabric-extension-handlers/entry-points/ExcerptInclude.ts';
import { getMigrationExtHandlerForRenderer } from '@confluence/fabric-extension-handlers/entry-points/MigrationExtension.ts';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { RendererExtensionHandlers } from '@confluence/fabric-extension-lib/entry-points/extensionTypes.ts';
import {
	EXTENSION_KEY,
	EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import { hasConfiguredTeamCalendar } from '@confluence/fabric-extension-lib/entry-points/macroParamUtils.ts';

import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { AnchorExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/AnchorExtensionHandler.ts';
import { ExpandExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExpandExtensionHandler.ts';
import { ExcerptExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExcerptExtensionHandler.ts';
import { DetailsExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsExtensionHandler.ts';
import { DetailsSummaryExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsSummaryExtensionHandler.ts';
import { resolveQuestionsMacroExtensionKey } from '@confluence/fabric-extension-lib/entry-points/macroKeyResolver.ts';
import { PagePropertiesReportRenderer } from '@confluence/page-properties-report-renderer';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import { isMigratedMacroImplEnabled } from '@confluence/macro-utils/entry-points/evaluateMacroEnablement.ts';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import { TeamCalendarMacroLoader } from '@confluence/team-calendar/entry-points/TeamCalendarMacroLoader.ts';

import RendererLoader from '../../components/Renderer/index.tsx';
import { populateExtensionProviders } from '../../components/Renderer/populateExtensionProviders.ts';

import { MacroAnalyticsWrapper } from '../extensions-common/MacroAnalyticsWrapper.tsx';
import {
	getTemplateExtensionHandler,
	getTemplateVariableInputExtensionHandler,
} from '../editor-extensions/template-variable-extension/index.ts';

import { CreateFromTemplateHandler } from './CreateFromTemplate.tsx';
import { LiveSearchHandler } from './LiveSearchHandler.tsx';
import { TableOfContentsHandler } from './TableOfContentsHandler.tsx';
import { MacroReference } from './MacroReference.tsx';
import { sendTrackingEvent } from './analytics.ts';

type Options = {
	adf: ADNode | string | null;
	appearance: string;
	contentId: string;
	spaceKey: string | undefined;
	showTemplateVariablesInPreview?: boolean;
	showTemplateInputInPreview?: boolean;
	shouldIncludeMacroHandlers?: boolean;
	macroRenderedOutput?: any;
	shouldIncludeMacroWithReferencesHandlers?: boolean;
	referentialityDataSourceProvider: ReferentialityDataSourceProvider;
	fromFabricEditor?: boolean;
	isPreviewMode?: boolean;
	contentType?: string;
	mediaToken?: string;
	annotationProvider?: AnnotationProviders;
	isInEditorRendererView?: boolean;
};

export const getExtensionHandlers = (
	{
		adf,
		contentId,
		appearance,
		showTemplateVariablesInPreview,
		showTemplateInputInPreview,
		spaceKey,
		shouldIncludeMacroHandlers = true,
		macroRenderedOutput,
		shouldIncludeMacroWithReferencesHandlers = false,
		referentialityDataSourceProvider,
		fromFabricEditor = false,
		isPreviewMode = false,
		contentType,
		mediaToken,
		annotationProvider,
		isInEditorRendererView,
	}: Options,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): RendererExtensionHandlers => {
	const getMacroExtensionHandler = getMacroExtensionHandlerForRenderer;
	const getTemplateHandler = showTemplateInputInPreview
		? getTemplateVariableInputExtensionHandler
		: getTemplateExtensionHandler;

	const templateHandler = showTemplateVariablesInPreview
		? { [EXTENSION_TYPE.TEMPLATE]: getTemplateHandler }
		: {};

	const macroWithReferences = shouldIncludeMacroWithReferencesHandlers
		? {
				[EXTENSION_TYPE.MACRO_WITH_REFERENCES]: getExtensionHandlerWithReferences({
					adf,
					referentialityDataSourceProvider,
				}),
			}
		: {};

	return shouldIncludeMacroHandlers
		? {
				[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandler(
					{
						adf,
						appearance,
						contentId,
						showTemplateVariablesInPreview,
						showTemplateInputInPreview,
						spaceKey,
						macroRenderedOutput,
						referentialityDataSourceProvider,
						fromFabricEditor,
						isPreviewMode,
						contentType,
						mediaToken,
						annotationProvider,
						isInEditorRendererView,
					},
					createAnalyticsEvent,
				),
				[EXTENSION_TYPE.MIGRATION]: getMigrationExtHandlerForRenderer({
					contentId,
					contextProviders: {
						ClassicEditorContextProvider: ClassicEditorContextProviderLazyLoader,
					},
				}),
				...macroWithReferences,
				...templateHandler,
			}
		: {};
};

const providers = {
	getExtensionHandlers,
	populateExtensionProviders,
	// CONFCLOUD-72035 - Make sure we are ALWAYS including the EditorLoaderLoader when loading
	//                   legacy macros or else the attachments macro WILL NOT WORK
	EditorLoaderLoader,
};

export const addSSRMetricsInfo = (isMacroSSRD: any, macroDefaultProps: any): void => {
	// if SSR Macros FF is disabled, page is not SSRd, or there are no macros on the page, do nothing
	if (!window.__SSR_RENDERED__ || !window?.__SSR_MACROS_INFO__) {
		return;
	}

	const { extensionKey } = macroDefaultProps;
	if (isMacroSSRD) {
		window.__SSR_MACROS_INFO__.successes[extensionKey] =
			(window.__SSR_MACROS_INFO__.successes[extensionKey] ?? 0) + 1;
		window.__SSR_MACROS_INFO__.successCount += 1;
	} else {
		window.__SSR_MACROS_INFO__.failures[extensionKey] =
			(window.__SSR_MACROS_INFO__.failures[extensionKey] ?? 0) + 1;
		window.__SSR_MACROS_INFO__.failureCount += 1;
	}
};

export const isFabricMacroSSRD = (macroDefaultProps: any): boolean => {
	const {
		attributes: { macroId },
	} = macroDefaultProps;
	return Boolean(window?.__LEGACY_MACRO_RENDERED_OUTPUT__?.find((el) => el.key === macroId));
};

export const getExtensionHandlerWithReferences = ({
	adf,
	referentialityDataSourceProvider,
}: {
	adf: any;
	referentialityDataSourceProvider: any;
}): ExtensionHandlerWithReferenceFn<any> => {
	const extensionHandlerWithReferences = (
		extension,
		extensionHandler: ({ references }) => JSX.Element,
	) => {
		return (
			<MacroReference
				adf={adf}
				extensionParams={extension}
				referentialityDataSourceProvider={referentialityDataSourceProvider}
			>
				{extensionHandler}
			</MacroReference>
		);
	};
	return extensionHandlerWithReferences;
};

export const getMacroExtensionHandlerForRenderer = (
	{
		adf,
		contentId,
		appearance,
		showTemplateVariablesInPreview,
		showTemplateInputInPreview,
		spaceKey,
		macroRenderedOutput,
		referentialityDataSourceProvider,
		fromFabricEditor,
		isPreviewMode,
		contentType,
		mediaToken,
		annotationProvider,
		isInEditorRendererView,
	}: Options,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): ExtensionHandler<any> => {
	const handler = createMacroExtensionHandler((extension, doc) => {
		let { extensionKey } = extension;
		const resolvedExtensionKey = resolveQuestionsMacroExtensionKey(extensionKey);
		if (resolvedExtensionKey !== extensionKey) {
			extensionKey = resolvedExtensionKey;
			extension = { ...extension, extensionKey: resolvedExtensionKey };
		}

		const attributes = getMacroAttributesFromADFNode(extension);

		const macroParams = extension?.parameters?.macroParams;
		const multiBodiedExtensionActions = doc?.[0]?.actions;

		const isBodiedMacro = extension?.type === 'bodiedExtension';
		const shouldUseDebouncedLegacyMacroRenderer = isBodiedMacro && isInEditorRendererView;

		// When isInEditorRendererView is true, we are rendering the macro in the editor renderer view.
		// In this scenario adf isn't passed to the macro extension handler as prop because we do not have access to
		// the adf from where we are using the macro extension handler. We can instead get the adf from the doc object provided to the handler.
		adf = isInEditorRendererView && !adf && !!doc ? doc?.[0] : adf;

		const attributesForAnalytics = {
			extensionType: extension?.type as string,
			...(extensionKey === 'excerpt-include'
				? { isExcerptNamePresent: !!macroParams?.name }
				: undefined),
			...(extensionKey === 'gadget' &&
				macroParams?.url?.value &&
				typeof location !== 'undefined' && {
					gadgetUrl: new URL(macroParams.url.value, location.origin).pathname,
				}),
		};

		const redactedAttributes =
			extensionKey === 'redactedMacroPlaceholder'
				? {
						extensionKey:
							macroParams?.redactedMacroPluginKey?.value ||
							macroParams?.redactedMacroExtensionKey?.value,
					}
				: undefined;
		const isMissingMacroParameters = macroParams?.isMissingRequiredParameters?.value;

		const contentHandler = (macroDefaultProps, cloudId, userId) => {
			const excerptMacroProps = {
				...macroDefaultProps,
				appearance,
			};

			const macroOutput = extension?.parameters?.macroOutput;

			if (extensionKey === 'toc' && macroParams && createAnalyticsEvent) {
				sendTrackingEvent(extensionKey, macroParams, createAnalyticsEvent, contentId);
			}

			const legacyMacroRenderer = () => {
				const isMacroSSRD =
					window.__SSR_RENDERED__ &&
					isFabricMacroSSRD(macroDefaultProps) &&
					!isMissingMacroParameters;
				addSSRMetricsInfo(isMacroSSRD, macroDefaultProps);
				return (
					<RendererExtensionContext.Provider value={providers}>
						{shouldUseDebouncedLegacyMacroRenderer ? (
							<DebouncedLegacyMacroRenderer
								{...macroDefaultProps}
								isTemplateEditorRenderer={showTemplateVariablesInPreview}
								macroRenderedOutputFromSSR={isMacroSSRD && macroRenderedOutput}
								macroOutput={macroOutput}
								isPreviewMode={isPreviewMode}
								isInEditorRendererView={isInEditorRendererView}
							/>
						) : (
							<LegacyMacroRenderer
								{...macroDefaultProps}
								isTemplateEditorRenderer={showTemplateVariablesInPreview}
								macroRenderedOutputFromSSR={isMacroSSRD && macroRenderedOutput}
								macroOutput={macroOutput}
								isPreviewMode={isPreviewMode}
							/>
						)}
					</RendererExtensionContext.Provider>
				);
			};

			switch (extensionKey) {
				case 'redactedMacroPlaceholder':
					return (
						<RedactedPlaceholderExtensionHandler
							{...macroDefaultProps}
							redactedAttributes={redactedAttributes}
						/>
					);
				case 'details':
					return (
						<DetailsExtensionHandler
							{...macroDefaultProps}
							spaceKey={spaceKey}
							Renderer={RendererLoader}
							isLegacyRenderer
							annotationProvider={annotationProvider}
						/>
					);
				case 'excerpt':
					return (
						<ExcerptExtensionHandler
							{...excerptMacroProps}
							spaceKey={spaceKey}
							Renderer={RendererLoader}
							isLegacyRenderer
							mediaToken={mediaToken}
							annotationProvider={annotationProvider}
						/>
					);
				case 'expand':
					return (
						<ExpandExtensionHandler
							{...macroDefaultProps}
							Renderer={RendererLoader}
							annotationProvider={annotationProvider}
							isLegacyRenderer
						/>
					);
				case 'create-from-template':
					return (
						<CreateFromTemplateHandler
							hasADF={!!adf}
							macroDefaultProps={macroDefaultProps}
							cloudId={cloudId}
							userId={userId}
							isPreviewMode={isPreviewMode}
							shouldLoadAfterPaint={fromFabricEditor}
						/>
					);
				case 'toc':
					return (
						<TableOfContentsHandler
							adf={adf}
							macroDefaultProps={macroDefaultProps}
							cloudId={cloudId}
							userId={userId}
							shouldLoadAfterPaint={fromFabricEditor}
						/>
					);
				case 'calendar':
					if (expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)) {
						return (
							<TeamCalendarMacroLoader
								extensionNode={extension}
								macroDefaultProps={macroDefaultProps}
							/>
						);
					}
					return legacyMacroRenderer();
				case 'livesearch':
					return (
						<LiveSearchHandler
							hasADF={!!adf}
							macroDefaultProps={macroDefaultProps}
							shouldLoadAfterPaint={fromFabricEditor}
						/>
					);
				case 'anchor':
					return <AnchorExtensionHandler {...macroDefaultProps} isSSR={false} />;
				case 'excerpt-include':
					if (contentId) {
						return (
							<ClassicEditorContextProviderLoader>
								<ExcerptIncludeExtensionHandler
									{...macroDefaultProps}
									Renderer={PageContentRenderer}
								/>
							</ClassicEditorContextProviderLoader>
						);
					}
				case 'detailssummary':
					return (
						<ClassicEditorContextProviderLoader isWithinEditor={false}>
							{isMigratedMacroImplEnabled('detailssummary') ? (
								<DetailsSummaryExtensionHandler
									macroDefaultProps={macroDefaultProps}
									macroParams={macroParams}
									Renderer={RendererLoader}
									isLegacyRenderer
								/>
							) : (
								<PagePropertiesReportRenderer
									macroDefaultProps={macroDefaultProps}
									macroParams={macroParams}
									Renderer={RendererLoader}
									isLegacyRenderer
								/>
							)}
						</ClassicEditorContextProviderLoader>
					);
				case 'blog-posts':
					if (isMigratedMacroImplEnabled('blog-posts')) {
						return (
							<ClassicEditorContextProviderLoader>
								<BlogPostsExtensionHandler
									{...macroDefaultProps}
									macroParams={extension?.parameters?.macroParams}
									Renderer={PageContentRenderer}
									appearance={appearance}
								/>
							</ClassicEditorContextProviderLoader>
						);
					}
					return legacyMacroRenderer();
				case 'recently-updated':
					if (isMigratedMacroImplEnabled('recently-updated')) {
						return (
							<RecentlyUpdatedMacroHandler
								macroDefaultProps={macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
							/>
						);
					}
					return legacyMacroRenderer();
				case 'profile-picture':
					return (
						<ProfilePictureMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					);
				case 'profile':
					return (
						<UserProfileMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					);
				case 'include':
					if (isMigratedMacroImplEnabled('include')) {
						return (
							<ClassicEditorContextProviderLoader>
								<PageIncludeExtensionHandler
									{...macroDefaultProps}
									macroParams={extension?.parameters?.macroParams}
									Renderer={PageContentRenderer}
									appearance={appearance}
								/>
							</ClassicEditorContextProviderLoader>
						);
					}
					return legacyMacroRenderer();
				case 'popular-labels':
					return isMigratedMacroImplEnabled('popular-labels') ? (
						<PopularLabelsMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={{
								...extension?.parameters?.macroParams,
								currentSpaceKey: spaceKey,
							}}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'contributors':
					return isMigratedMacroImplEnabled('contributors') ? (
						<ContributorsMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'viewpdf':
					return isMigratedMacroImplEnabled('viewpdf') ? (
						<ViewPdfMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'widget':
					return isMigratedMacroImplEnabled('widget') ? (
						<WidgetMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'iframe':
					return isMigratedMacroImplEnabled('iframe') ? (
						<IFrameMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'contentbylabel':
					return isMigratedMacroImplEnabled('contentbylabel') ? (
						<ClassicEditorContextProviderLoader>
							<ContentByLabelExtensionHandler
								{...macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								Renderer={PageContentRenderer}
								appearance={appearance}
							/>
						</ClassicEditorContextProviderLoader>
					) : (
						legacyMacroRenderer()
					);
				case 'tasks-report-macro':
					return isMigratedMacroImplEnabled('tasks-report-macro') ? (
						<TasksReportMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'pagetree':
					return isMigratedMacroImplEnabled('pagetree') ? (
						<ClassicEditorContextProviderLoader>
							<PageTreeExtensionHandler
								{...macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								Renderer={PageContentRenderer}
								appearance={appearance}
							/>
						</ClassicEditorContextProviderLoader>
					) : (
						legacyMacroRenderer()
					);
				case 'pagetreesearch':
					return isMigratedMacroImplEnabled('pagetreesearch') ? (
						<PageTreeSearchMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'content-report-table':
					return (
						<ContentReportTableMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={{
								...extension?.parameters?.macroParams,
								currentSpaceKey: spaceKey,
							}}
						/>
					);
				case 'attachments':
					return isMigratedMacroImplEnabled('attachments') ? (
						<AttachmentsMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'change-history':
					return isMigratedMacroImplEnabled('change-history') ? (
						<ChangeHistoryMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					) : (
						legacyMacroRenderer()
					);
				case 'questionslist-macro':
					return legacyMacroRenderer();

				case 'questionslist-macro-native':
					return (
						<GlobalQuestionsListMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					);
				case 'hide':
					if (expVal('platform_editor_hide_extension_renderer_support', 'isEnabled', false)) {
						return (
							<Fragment>
								<div data-extension-key="hide" />
								<MacroExperienceSuccess
									name={getExperienceName(RENDERER, extension)}
									extensionKey={extensionKey}
									mode={RENDERER}
									attributes={attributes}
									contentId={contentId}
								/>
							</Fragment>
						);
					}
					return legacyMacroRenderer();
				// generic macro renderer
				default:
					return legacyMacroRenderer();
			}
		};

		return (
			<RendererActionsContext>
				<MacroReference
					adf={adf}
					extensionParams={extension}
					referentialityDataSourceProvider={referentialityDataSourceProvider}
				>
					{({ references }) => {
						const macroDefaultProps: ExtensionHandlerProps & { contentType?: string } = {
							node: extension,
							references,
							contentId,
							contentType,
							showTemplateVariablesInPreview: showTemplateVariablesInPreview || false,
							showTemplateInputInPreview: showTemplateInputInPreview || false,
							spaceKey: spaceKey || '',
							mode: RENDERER,
							extensionKey,
							attributes,
							multiBodiedExtensionActions,
							isConnectMacro: Boolean(doc?.[0]?.isConnectMacro),
						};
						return (
							<Fragment>
								<MacroExperienceStart
									contentId={contentId}
									name={getExperienceName(RENDERER, extension)}
									mode={RENDERER}
									extensionKey={extensionKey}
									attributes={attributes}
									timeout={ExperienceTimeout.MACRO_LOAD}
								/>
								<MacroAnalyticsWrapper
									extensionKey={extensionKey}
									attributes={{
										...attributesForAnalytics,
										...redactedAttributes,
									}}
									node={extension}
								>
									<SessionData>
										{({ cloudId, userId }) => contentHandler(macroDefaultProps, cloudId, userId)}
									</SessionData>
								</MacroAnalyticsWrapper>
							</Fragment>
						);
					}}
				</MacroReference>
			</RendererActionsContext>
		);
	});

	return (extension, doc) => {
		if (extension?.extensionKey?.startsWith(EXTENSION_KEY.NATIVE_EMBED)) {
			return null;
		}
		// Team Calendar stays on the product handler after manifests are ready to preserve its SSR
		// placeholder. When its required calendar ID is absent, step aside so the manifest renderer
		// can apply the standard missing-configuration placeholder instead of loading the macro.
		if (
			extension?.extensionKey === EXTENSION_KEY.TEAM_CALENDARS &&
			!hasConfiguredTeamCalendar(extension?.parameters?.macroParams) &&
			expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)
		) {
			return null;
		}
		return handler(extension, doc);
	};
};
